export interface CognitoRuntimeConfig {
  aws_project_region: string;
  aws_cognito_region: string;
  aws_user_pools_id: string;
  aws_user_pools_web_client_id: string;
  oauth: {
    domain: string;
    scope: string[];
    redirectSignIn: string;
    redirectSignOut: string;
    responseType: string;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isNonEmptyString);
}

export function parseCognitoRuntimeConfig(value: unknown): CognitoRuntimeConfig {
  if (!isRecord(value) || !isRecord(value['oauth'])) {
    throw new Error('Cognito configuration response must be an object with an oauth object.');
  }

  const oauth = value['oauth'];
  const projectRegion = value['aws_project_region'];
  const cognitoRegion = value['aws_cognito_region'];
  const userPoolId = value['aws_user_pools_id'];
  const userPoolClientId = value['aws_user_pools_web_client_id'];
  const domain = oauth['domain'];
  const scope = oauth['scope'];
  const redirectSignIn = oauth['redirectSignIn'];
  const redirectSignOut = oauth['redirectSignOut'];
  const responseType = oauth['responseType'];

  if (
    !isNonEmptyString(projectRegion) ||
    !isNonEmptyString(cognitoRegion) ||
    !isNonEmptyString(userPoolId) ||
    !isNonEmptyString(userPoolClientId) ||
    !isNonEmptyString(domain) ||
    !isStringArray(scope) ||
    scope.length === 0 ||
    !isNonEmptyString(redirectSignIn) ||
    !isNonEmptyString(redirectSignOut) ||
    !isNonEmptyString(responseType) ||
    (responseType !== 'code' && responseType !== 'token')
  ) {
    throw new Error('Cognito configuration response is missing required or valid values.');
  }

  return {
    aws_project_region: projectRegion,
    aws_cognito_region: cognitoRegion,
    aws_user_pools_id: userPoolId,
    aws_user_pools_web_client_id: userPoolClientId,
    oauth: {
      domain,
      scope,
      redirectSignIn,
      redirectSignOut,
      responseType,
    },
  };
}

export async function loadCognitoRuntimeConfig(serverUrl: string): Promise<CognitoRuntimeConfig> {
  const baseUrl = serverUrl.endsWith('/') ? serverUrl : `${serverUrl}/`;
  const endpoint = new URL('auth/config', baseUrl);
  const response = await fetch(endpoint, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(`Cognito configuration endpoint returned HTTP ${response.status}.`);
  }

  return parseCognitoRuntimeConfig(await response.json());
}
